import { EasyUIAppPayload, EasyUIComponents } from "../types";
import { createApp, h, onBeforeMount, onBeforeUnmount, Ref, ref, watch } from "vue";
import { createPinia } from "pinia";
import { getVuetify } from "../plugins/vuetify";
import { queryLastElementById } from "../../shared/utils/uiHelpers";
import { LoadingStatus } from "@/src/shared/enums/general";
import VueDOMPurifyHTML from "vue-dompurify-html";

export const mountEasyUIApp = (
  component: EasyUIComponents,
  { props, hiddenInputId, wrapperId, componentPrefix, triggerUpdateEvent, localeKeys }: EasyUIAppPayload
) => {
  const app = createApp({
    setup() {
      const hiddenInput = ref<HTMLInputElement | null>(null);
      const modelValue = ref<string | null>(null);
      const hiddenInputValueObserver = ref<MutationObserver | null>(null);
      fetchLocaleStoreLocales(localeKeys);

      watch(modelValue, newValue => updateModelValue(newValue, hiddenInput, triggerUpdateEvent));
      onBeforeMount(() => setupBeforeMount({ hiddenInputId, modelValue, hiddenInput, hiddenInputValueObserver }));
      onBeforeUnmount(() => disconnectObserver(hiddenInputValueObserver));

      return { modelValue };
    },

    render() {
      return h(component, {
        ...props,
        loading: window.EasySettings.globalSettingsLoading.value === LoadingStatus.Loading,
        modelValue: this.modelValue,
        "onUpdate:modelValue": (value: string) => (this.modelValue = value),
      });
    },
  });

  const wrapper = queryLastElementById(wrapperId);
  wrapper.classList.add(`${componentPrefix}--html-erb`);

  app.use(createPinia());
  app.use(getVuetify());
  app.use(VueDOMPurifyHTML);

  app.mount(wrapper);
};

export const fetchLocaleStoreLocales = async (localeKeys: string[]) => {
  await window.EasyLocale.fetchLocales(localeKeys);
};

const setupBeforeMount = ({
  hiddenInputId,
  modelValue,
  hiddenInput,
  hiddenInputValueObserver,
}: {
  hiddenInputId: string;
  modelValue: Ref<string | null>;
  hiddenInput: Ref<HTMLInputElement | null>;
  hiddenInputValueObserver: Ref<MutationObserver | null>;
}) => {
  hiddenInput.value = queryLastElementById(hiddenInputId) as HTMLInputElement;
  if (hiddenInput.value) modelValue.value = hiddenInput.value.value;
  const updateModelValue = (newValue: string) => (modelValue.value = newValue);
  hiddenInputValueObserver.value = setupHiddenInputObserver(hiddenInput.value, updateModelValue);
};

const disconnectObserver = (hiddenInputValueObserver: Ref<MutationObserver | null>) => {
  if (!hiddenInputValueObserver.value) return;

  hiddenInputValueObserver.value.disconnect();
};

const updateModelValue = (
  newValue: string | null,
  hiddenInput: Ref<HTMLInputElement | null>,
  triggerUpdateEvent: (updatedInput: HTMLInputElement) => void
) => {
  if (!hiddenInput.value || newValue === hiddenInput.value.value) return;
  hiddenInput.value.value = newValue || "";
  triggerUpdateEvent(hiddenInput.value);
};

export const setupHiddenInputObserver = (hiddenInput: HTMLInputElement, updateMethod: (value: string) => void) => {
  if (!hiddenInput) return null;

  const observer = new MutationObserver((mutations: MutationRecord[]) => {
    mutations.forEach(mutation => {
      if (mutation.type === "attributes" && mutation.attributeName === "value") {
        updateMethod(hiddenInput.value);
      }
    });
  });

  observer.observe(hiddenInput, { attributes: true });

  return observer;
};
